import { useToast } from "@/hooks/use-toast";
import {
  notificationPreferencesService,
  type AdminNotificationPreferences,
} from "@/lib/api/notificationPreferences.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const QUERY_KEY = ["admin", "notif-prefs"] as const;

export function useNotificationPreferences() {
  return useQuery<AdminNotificationPreferences>({
    queryKey: QUERY_KEY,
    queryFn: notificationPreferencesService.get,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    AdminNotificationPreferences,
    Error,
    Partial<AdminNotificationPreferences>,
    { previous?: AdminNotificationPreferences }
  >({
    mutationFn: (patch) => notificationPreferencesService.update(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous =
        queryClient.getQueryData<AdminNotificationPreferences>(QUERY_KEY);
      if (previous) {
        queryClient.setQueryData<AdminNotificationPreferences>(QUERY_KEY, {
          ...previous,
          ...patch,
        });
      }
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(QUERY_KEY, ctx.previous);
      }
      toast({
        title: "Couldn’t save preference",
        variant: "destructive",
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
}
